/**
 * Remove price filter params when min/max match the default full range (0 → max).
 * Otherwise Shopify keeps filter.v.price.* in the URL and applies a price filter on every facet change.
 */
function stripDefaultPriceFiltersFromParams(params, formId) {
  const form = document.getElementById(formId);
  if (!form) {
    return;
  }
  const priceRange = form.querySelector('price-range');
  if (!priceRange) {
    return;
  }

  let minEl = priceRange.querySelector('.number-slider .rulerinput-min');
  let maxEl = priceRange.querySelector('.number-slider .rulerinput-max');
  if (!minEl || !maxEl) {
    minEl = priceRange.querySelector('input[name*="gte"]');
    maxEl = priceRange.querySelector('input[name*="lte"]');
  }
  if (!minEl || !maxEl) {
    const named = priceRange.querySelectorAll('input[type="text"][name], input[type="number"][name]');
    if (named.length >= 2) {
      minEl = named[0];
      maxEl = named[1];
    }
  }
  if (!minEl?.name || !maxEl?.name) {
    return;
  }

  const parseVal = (el) => {
    const raw =
      el.dataset.rawValue != null && el.dataset.rawValue !== ''
        ? String(el.dataset.rawValue)
        : String(el.value || '').replace(/,/g, '');
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : NaN;
  };

  const parseParamNum = (key) => {
    const raw = params.get(key);
    if (raw == null || raw === '') return NaN;
    return parseFloat(String(raw).replace(/,/g, ''));
  };

  const paramKeys = [...params.keys()];
  // Do not use k.includes('lte'): "filter.v.price.gte" contains "lte" inside the word "filter".
  const gteKeyFromParams = paramKeys.find((k) => k.includes('price') && k.endsWith('.gte'));
  const lteKeyFromParams = paramKeys.find((k) => k.includes('price') && k.endsWith('.lte'));

  // Prefer merged URLSearchParams keys: DOM input `name` can differ from serialized keys while values match.
  let numMin = gteKeyFromParams ? parseParamNum(gteKeyFromParams) : NaN;
  let numMax = lteKeyFromParams ? parseParamNum(lteKeyFromParams) : NaN;
  if (!Number.isFinite(numMin)) numMin = parseParamNum(minEl.name);
  if (!Number.isFinite(numMax)) numMax = parseParamNum(maxEl.name);
  if (!Number.isFinite(numMin)) numMin = parseVal(minEl);
  if (!Number.isFinite(numMax)) numMax = parseVal(maxEl);
  const maxAttr = maxEl.getAttribute('max');
  const dataRm = priceRange.dataset.rangeMax || priceRange.getAttribute('data-range-max');
  let rangeMax = parseFloat(String(maxAttr || '').replace(/,/g, ''));
  if (!Number.isFinite(rangeMax) || rangeMax <= 0) {
    rangeMax = parseFloat(String(dataRm || '').replace(/,/g, ''));
  }

  if (!Number.isFinite(rangeMax) || rangeMax <= 0) {
    return;
  }

  const minIsDefault = !Number.isFinite(numMin) || numMin <= 0;
  const maxIsFullRange = Number.isFinite(numMax) && numMax >= rangeMax - 0.01;

  if (minIsDefault && maxIsFullRange) {
    const gk = gteKeyFromParams || minEl.name;
    const lk = lteKeyFromParams || maxEl.name;
    if (gk) params.delete(gk);
    if (lk) params.delete(lk);
  }
}

/** Merge facet forms by id (same rules as FacetFiltersForm submit). Used by Apply button so it cannot bypass strip/merge. */
function mergeFacetFormParams(mergeFormIds) {
  const mergedParams = new URLSearchParams();
  document.querySelectorAll('facet-filters-form .no-js').forEach((el) => el.remove());
  document.querySelectorAll('facet-filters-form form').forEach((formEl) => {
    if (!mergeFormIds.includes(formEl.id)) return;
    const formParams = new URLSearchParams(new FormData(formEl));
    formParams.forEach((value, key) => {
      if (value === '' && (key.includes('filter.v.price') || key.includes('filter.p.m.price'))) {
        return;
      }
      if (key.includes('filter.v.m.') || key === 'filter.v.availability') {
        const existingValues = mergedParams.getAll(key);
        if (!existingValues.includes(value)) {
          mergedParams.append(key, value);
        }
      } else {
        mergedParams.set(key, value);
      }
    });
  });
  return mergedParams;
}

/** Like debounce() but exposes .cancel() so Apply can skip a pending facet AJAX before a single renderPage. */
function cancelableDebounce(fn, wait) {
  let t;
  const wrapped = function (...args) {
    clearTimeout(t);
    t = setTimeout(() => {
      t = null;
      fn.apply(this, args);
    }, wait);
  };
  wrapped.cancel = () => {
    clearTimeout(t);
    t = null;
  };
  return wrapped;
}

// Keep the lazy-loaded facet controller independent from global.js. On the
// MacBook Pro landing page facets.js is intentionally warmed during parsing,
// so it can execute before the deferred global bundle defines its helper.
function closeFacetDetailsOnEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetails = event.target.closest('details[open]');
  if (!openDetails) return;
  const summary = openDetails.querySelector('summary');
  openDetails.removeAttribute('open');
  if (summary) {
    summary.setAttribute('aria-expanded', 'false');
    summary.focus();
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this._debouncedSubmit = cancelableDebounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

  }

  connectedCallback() {
    if (this._abort && !this._abort.signal.aborted) return;
    // AbortController lets disconnectedCallback yank EVERY listener
    // attached via { signal } in one call. Cleaner than tracking
    // bound-handler references for every single addEventListener.
    this._abort = new AbortController();
    const signal = this._abort.signal;

    const facetForm = this.querySelector('form');
    // Null-guard: a malformed render or external mutation could
    // strip the inner <form>; without this guard the next line throws.
    if (facetForm) {
      facetForm.addEventListener('input', (e) => this._debouncedSubmit(e), { signal });
      facetForm.addEventListener('change', (e) => this._debouncedSubmit(e), { signal });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', closeFacetDetailsOnEscape, { signal });
  }

  disconnectedCallback() {
    // Cancel pending debounced submit + remove all { signal }-bound
    // listeners. Important on collection filter changes where Shopify
    // replaces facet-filters-form via Section Rendering - without this
    // the old instance kept firing onSubmitHandler against stale DOM.
    this.cancelPendingSubmit();
    if (this._abort) this._abort.abort();
  }

  cancelPendingSubmit() {
    if (this._debouncedSubmit && typeof this._debouncedSubmit.cancel === 'function') {
      this._debouncedSubmit.cancel();
    }
  }

  static cancelPendingSubmitsEverywhere() {
    document.querySelectorAll('facet-filters-form').forEach((el) => {
      if (typeof el.cancelPendingSubmit === 'function') el.cancelPendingSubmit();
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev && window.location.pathname === FacetFiltersForm.pathnamePrev) return;
      FacetFiltersForm.renderPage(searchParams, { basePath: window.location.pathname }, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static cacheContext() {
    return JSON.stringify([
      window.location.origin,
      window.Shopify?.routes?.root || '/',
      window.Shopify?.country || '',
      window.Shopify?.currency?.active || '',
      document.documentElement?.lang || ''
    ]);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.pendingAnalytics = updateURLHash ? window.AlAsilDiscovery?.facetChanges(window.location.search, searchParams) : null;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const renderPathname = event?.basePath || window.location.pathname;
    FacetFiltersForm.pathnamePrev = renderPathname;
    // A cached response can render synchronously while an older network
    // request is still in flight. Cancel it here (not only when starting a
    // second fetch), otherwise the older response can overwrite the latest
    // quick-filter choice after the cached result has already painted.
    if (FacetFiltersForm.activeAbortController) {
      FacetFiltersForm.activeAbortController.abort();
      FacetFiltersForm.activeAbortController = null;
    }
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    // Guard the chain - under section-render races (e.g. AJAX result swap or
    // page transition), ProductGridContainer or its .collection child can be
    // missing for one frame and the unguarded chain throws
    // "Cannot read properties of null (reading 'querySelector')".
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = productGridContainer && productGridContainer.querySelector('.collection');
    // Quick filters keep the existing products visible while the warmed
    // section is swapped. The generic collection loading overlay paints a
    // translucent layer across the whole grid and reads as a white flash.
    if (collectionEl && !event?.quickFilter) collectionEl.classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    // Commit navigation state before a cached section can render
    // synchronously and dispatch facets:grid-updated. Otherwise listeners
    // read the previous URL and revert the selector's active state.
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams, renderPathname);

    sections.forEach((section) => {
      const url = `${renderPathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url && element.context === FacetFiltersForm.cacheContext();

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

  }

  static renderSectionFromFetch(url, event) {
    const context = FacetFiltersForm.cacheContext();
    const warmKey = context + url;
    const warmRequest = FacetFiltersForm.prefetchRequests[warmKey];
    if (warmRequest) {
      warmRequest.then(() => {
        if (context !== FacetFiltersForm.cacheContext() || !FacetFiltersForm.isCurrentSectionURL(url)) return;

        const filterDataUrl = (element) => element.url === url && element.context === FacetFiltersForm.cacheContext();
        if (FacetFiltersForm.filterData.some(filterDataUrl)) {
          FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
        } else {
          FacetFiltersForm.renderSectionFromFetch(url, event);
        }
      });
      return;
    }

    // Abort previous in-flight request to prevent out-of-order rendering
    if (FacetFiltersForm.activeAbortController) {
      FacetFiltersForm.activeAbortController.abort();
    }
    const controller = new AbortController();
    FacetFiltersForm.activeAbortController = controller;

    fetch(url, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (controller.signal.aborted || FacetFiltersForm.activeAbortController !== controller || context !== FacetFiltersForm.cacheContext()) return;
        const html = responseText;
        FacetFiltersForm.validateSectionHTML(html);
        // Cap cache at 20 entries to prevent unbounded memory growth
        if (FacetFiltersForm.filterData.length >= 20) {
          FacetFiltersForm.filterData = FacetFiltersForm.filterData.slice(-10);
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url, context }];
        if (!FacetFiltersForm.isCurrentSectionURL(url)) return;
        FacetFiltersForm.renderSectionHTML(html, event);
      })
      .catch(e => {
        if (e.name === 'AbortError' || controller.signal.aborted || FacetFiltersForm.activeAbortController !== controller || context !== FacetFiltersForm.cacheContext()) return;
        // Keep the native route as the recovery path, never cache an error
        // page or leave controls permanently loading. Obsolete reads stay silent.
        if (!FacetFiltersForm.isCurrentSectionURL(url)) return;
        const fallback = new URL(url, window.location.origin);
        fallback.searchParams.delete('section_id');
        window.location.assign(fallback.pathname + fallback.search);
      })
      .finally(() => {
        if (FacetFiltersForm.activeAbortController === controller) {
          FacetFiltersForm.activeAbortController = null;
        }
      });
  }

  static validateSectionHTML(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    if (!parsed.getElementById('ProductGridContainer')) throw new Error('Missing product section');
  }

  static isCurrentSectionURL(url) {
    const requestedURL = new URL(url, window.location.origin);
    const requestedParams = requestedURL.searchParams;
    requestedParams.delete('section_id');
    return requestedURL.pathname === FacetFiltersForm.pathnamePrev && requestedParams.toString() === FacetFiltersForm.searchParamsPrev;
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSectionHTML(html, event);
  }

  /** Paint results first; refresh the much larger facet controls on the next frame. */
  static renderSectionHTML(html, event) {
    if (event?.quickFilter && event.requestSequence !== collectionQuickFilterSequence) return;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderProductGridContainer(parsedHTML, event);
    FacetFiltersForm.renderProductCount(parsedHTML);
    const interactions = FacetFiltersForm.pendingAnalytics;
    FacetFiltersForm.pendingAnalytics = null;
    interactions?.forEach(({ event_name, ...fields }) => window.AlAsilDiscovery?.emit(event_name, fields));

    if (FacetFiltersForm.pendingFilterRenderTimer) {
      window.clearTimeout(FacetFiltersForm.pendingFilterRenderTimer);
      FacetFiltersForm.pendingFilterRenderTimer = null;
    }
    if (FacetFiltersForm.pendingFilterIdleId && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(FacetFiltersForm.pendingFilterIdleId);
      FacetFiltersForm.pendingFilterIdleId = null;
    }

    const renderDeferredFilters = () => {
      FacetFiltersForm.pendingFilterIdleId = null;
      if (event?.quickFilter && event.requestSequence !== collectionQuickFilterSequence) return;
      FacetFiltersForm.renderFilters(parsedHTML, event);
    };

    if (event?.quickFilter) {
      // Do not block the product-grid reveal with the much heavier
      // facet DOM refresh. Stale work is cancelled by requestSequence above.
      FacetFiltersForm.pendingFilterRenderTimer = window.setTimeout(() => {
        FacetFiltersForm.pendingFilterRenderTimer = null;
        if ('requestIdleCallback' in window) {
          FacetFiltersForm.pendingFilterIdleId = window.requestIdleCallback(renderDeferredFilters, { timeout: 500 });
        } else {
          renderDeferredFilters();
        }
      }, 520);
    } else {
      window.requestAnimationFrame(renderDeferredFilters);
    }
  }

  /** Warm likely collection-filter destinations without changing the UI. */
  static prefetchSection(url) {
    const context = FacetFiltersForm.cacheContext();
    const warmKey = context + url;
    if (!url || FacetFiltersForm.filterData.some((entry) => entry.url === url && entry.context === context)) return Promise.resolve();
    if (FacetFiltersForm.prefetchRequests[warmKey]) return FacetFiltersForm.prefetchRequests[warmKey];

    const request = fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Prefetch failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (context !== FacetFiltersForm.cacheContext()) return;
        FacetFiltersForm.validateSectionHTML(html);
        FacetFiltersForm.filterData = FacetFiltersForm.filterData.filter((entry) => entry.url !== url || entry.context !== context);
        if (FacetFiltersForm.filterData.length >= 20) {
          FacetFiltersForm.filterData = FacetFiltersForm.filterData.slice(-10);
        }
        FacetFiltersForm.filterData.push({ html, url, context });
      })
      .catch(() => {})
      .finally(() => {
        delete FacetFiltersForm.prefetchRequests[warmKey];
      });

    FacetFiltersForm.prefetchRequests[warmKey] = request;
    return request;
  }

  static renderProductGridContainer(parsedHTML, event) {
    const source = parsedHTML.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (source && target) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.innerHTML = source.innerHTML;
      const incomingGrid = target.querySelector('#product-grid');

      // Keep exactly one product grid in the live DOM. The previous crossfade
      // re-appended the old #product-grid as an absolutely positioned sibling
      // for up to 1.2s while the new grid was already present. On mobile that
      // second layer could escape its intended geometry and visibly duplicate
      // the cards. Animate only the newly rendered grid instead; stale section
      // responses are already rejected by requestSequence/isCurrentSectionURL.
      if (event?.quickFilter && incomingGrid && !reduceMotion) {
        incomingGrid.classList.add('collection-quick-filter__incoming-grid');
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          if (event.requestSequence !== collectionQuickFilterSequence || !incomingGrid.isConnected) return;
          incomingGrid.classList.add('is-entering');
          window.setTimeout(() => {
            if (!incomingGrid.isConnected) return;
            incomingGrid.classList.remove('collection-quick-filter__incoming-grid', 'is-entering');
          }, 320);
        }));
      }

      document.dispatchEvent(new CustomEvent('facets:grid-updated', {
        detail: { requestSequence: event?.requestSequence || null }
      }));
    }
  }

  static renderProductCount(parsedHTML) {
    const source = parsedHTML.getElementById('ProductCount');
    if (!source) return;
    const count = source.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    // Null-guard: layout variants without ProductCount in the DOM
    // (e.g. empty result page) shouldn't throw.
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(parsedHTML, event) {

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FacetFiltersFormMobileSheet .js-filter');
    const eventTarget = event?.target && typeof event.target.closest === 'function' ? event.target : null;
    const matchesIndex = (element) => {
      const jsFilter = eventTarget ? eventTarget.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      // Update elements with matching data-index, but skip bottom sheet (it has its own update cycle)
      document.querySelectorAll(`.js-filter[data-index="${element.dataset.index}"]`).forEach(el => {
        // For bottom sheet filters, only sync checkbox states (preserve native <details> behavior)
        if (el.closest('#filter-sheet')) {
          FacetFiltersForm.syncBottomSheetCheckboxes(element, el);
          return;
        }
        el.innerHTML = element.innerHTML;
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.updateMobileFilterButtonBadge(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && eventTarget) FacetFiltersForm.renderCounts(countsToRender, eventTarget.closest('.js-filter'));

    document.dispatchEvent(new CustomEvent('facets:updated'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      // Null-guard: the live page may not contain this selector
      // (desktop-only or mobile-only layouts).
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateMobileFilterButtonBadge(parsedHTML) {
    const source = parsedHTML.querySelector('.active-facets-mobile');
    const count = source
      ? source.querySelectorAll('facet-remove:not(.active-facets__button-wrapper)').length
      : 0;

    const mobileBadge = document.querySelector('[data-mobile-filter-badge]');
    if (mobileBadge) {
      mobileBadge.textContent = count === 0 ? '' : `(${count})`;
      if (count === 0) {
        mobileBadge.setAttribute('hidden', '');
      } else {
        mobileBadge.removeAttribute('hidden');
      }
    }

    const desktopBadge = document.querySelector('[data-desktop-filter-badge]');
    if (desktopBadge) {
      desktopBadge.textContent = count === 0 ? '' : `(${count})`;
      if (count === 0) {
        desktopBadge.setAttribute('hidden', '');
      } else {
        desktopBadge.removeAttribute('hidden');
      }
    }
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      if (!source) return;
      // Null-guard: desktop-only layouts may not have these selectors.
      const target = document.querySelector(selector);
      if (target) target.innerHTML = source.innerHTML;
    });

    // Null-guard: desktop layouts have no FacetFiltersFormMobile or
    // menu-drawer ancestor - don't throw on those pages.
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm && mobileForm.closest('menu-drawer');
    if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
  }

  static syncBottomSheetCheckboxes(sourceElement, targetElement) {
    // Sync checkbox states without replacing innerHTML (preserves <details> behavior)
    const sourceCheckboxes = sourceElement.querySelectorAll('input[type="checkbox"]');
    const targetCheckboxes = targetElement.querySelectorAll('input[type="checkbox"]');
    
    sourceCheckboxes.forEach((sourceCheckbox) => {
      const targetCheckbox = targetElement.querySelector(`input[name="${sourceCheckbox.name}"][value="${sourceCheckbox.value}"]`);
      if (targetCheckbox) {
        targetCheckbox.checked = sourceCheckbox.checked;
      }
    });
    
    // Update badge count
    const sourceBadge = sourceElement.querySelector('.bottom-sheet__filter-badge');
    const targetBadge = targetElement.querySelector('.bottom-sheet__filter-badge');
    if (sourceBadge && targetBadge) {
      targetBadge.textContent = sourceBadge.textContent;
      targetBadge.style.display = sourceBadge.style.display;
    } else if (!sourceBadge && targetBadge) {
      targetBadge.remove();
    } else if (sourceBadge && !targetBadge) {
      // Add badge if needed
      const summary = targetElement.querySelector('.bottom-sheet__filter-header');
      if (summary) {
        const newBadge = sourceBadge.cloneNode(true);
        summary.insertBefore(newBadge, summary.querySelector('.bottom-sheet__filter-chevron'));
      }
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;
    const countSel = '.facets-summary__count, .facets-summary__badge, .facets__selected';
    const targetElement = target.querySelector(countSel);
    const sourceElement = source.querySelector(countSel);

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector(countSel).outerHTML = source.querySelector(countSel).outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams, pathname = window.location.pathname) {
    history.pushState({ searchParams }, '', `${pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    // Null-guard: if #product-grid is missing (e.g. empty collection
    // mid-rerender), return an empty list rather than throw.
    const grid = document.getElementById('product-grid');
    if (!grid) return [];
    return [
      {
        section: grid.dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.srcElement.classList.contains('mobile-facets__checkbox')) {
      const form = event.target.closest('form');
      const searchParams = new URLSearchParams(this.createSearchParams(form));
      stripDefaultPriceFiltersFromParams(searchParams, form.id);
      this.onSubmitForm(searchParams.toString(), event);
    } else {
      const triggerForm = event.target.closest('form');
      const triggerId = triggerForm && triggerForm.id;
      const isMobile = triggerId === 'FacetFiltersFormMobile';

      /** Only merge the facet form that triggered input + sort, so bottom-sheet state does not get overwritten by hidden desktop/mobile forms. */
      let mergeFormIds;
      if (triggerId === 'FacetFiltersFormMobileSheet') {
        mergeFormIds = ['FacetSortForm', 'FacetFiltersFormMobileSheet'];
      } else if (triggerId === 'FacetFiltersFormMobile') {
        mergeFormIds = ['FacetSortForm', 'FacetFiltersFormMobile'];
      } else if (triggerId === 'FacetSortForm' || triggerId === 'FacetSortDrawerForm') {
        mergeFormIds = ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'];
      } else {
        mergeFormIds = ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'];
      }

      const mergedParams = mergeFacetFormParams(mergeFormIds);

      const stripFormId =
        triggerId &&
        ['FacetFiltersForm', 'FacetFiltersFormMobile', 'FacetFiltersFormMobileSheet'].includes(triggerId)
          ? triggerId
          : isMobile
            ? 'FacetFiltersFormMobile'
            : 'FacetFiltersForm';
      stripDefaultPriceFiltersFromParams(mergedParams, stripFormId);

      this.onSubmitForm(mergedParams.toString(), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.prefetchRequests = {};
FacetFiltersForm.pendingFilterRenderTimer = null;
FacetFiltersForm.pendingFilterIdleId = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.pathnamePrev = window.location.pathname;
customElements.get('facet-filters-form') || customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/** Swatch facet rows: server leaves .active stale on the open <details> (only renderCounts runs). Sync from native :checked. */
function syncFacetSwatchActiveFromInputs(root = document) {
  root.querySelectorAll('.swatch-input__input[type="checkbox"]').forEach((input) => {
    if (!input.closest('facet-filters-form')) return;
    const row = input.closest('.facet-checkbox--swatch, .mobile-facets__label.facet-checkbox--swatch');
    if (row) row.classList.toggle('active', input.checked);
  });
}
document.addEventListener('input', (e) => {
  const t = e.target;
  if (!t.matches?.('.swatch-input__input') || t.type !== 'checkbox') return;
  if (!t.closest('facet-filters-form')) return;
  const row = t.closest('.facet-checkbox--swatch, .mobile-facets__label.facet-checkbox--swatch');
  if (row) row.classList.toggle('active', t.checked);
});
document.addEventListener('facets:updated', () => syncFacetSwatchActiveFromInputs());

// price code change
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this._abort = new AbortController();
    const signal = this._abort.signal;

    const onRange = this.onRangeChange.bind(this);
    this.querySelectorAll('.range-slider input').forEach((element) => {
      element.addEventListener('change', onRange, { signal });
      element.addEventListener('input', onRange, { signal });
    });
    this.querySelectorAll('.number-slider input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this), { signal });
      element.addEventListener('input', this.formatNumberInput.bind(this), { signal });
      element.addEventListener('focus', this.onFocus.bind(this), { signal });
      element.addEventListener('blur', this.onBlur.bind(this), { signal });
      this.formatValue(element);
    });
    this.syncBottomSheetReadoutFromInputs();
  }

  disconnectedCallback() {
    if (this._abort) this._abort.abort();
  }

  syncBottomSheetReadoutFromInputs() {
    const minEl = this.querySelector('[data-bottom-sheet-price-min]');
    const maxEl = this.querySelector('[data-bottom-sheet-price-max]');
    const numberInputs = this.querySelectorAll('.number-slider input');
    if (!minEl || !maxEl || numberInputs.length < 2) return;
    const a = numberInputs[0].dataset.rawValue || this.unformatNumber(numberInputs[0].value);
    const b = numberInputs[1].dataset.rawValue || this.unformatNumber(numberInputs[1].value);
    minEl.textContent = this.formatNumber(String(a === '' ? 0 : a));
    maxEl.textContent = this.formatNumber(String(b === '' ? 0 : b));
  }

  formatNumber(num) {
    if (!num && num !== 0) return '';
    return Math.round(Number(String(num).replace(/,/g, ''))).toLocaleString('en-US');
  }

  unformatNumber(str) {
    if (!str) return '';
    return String(str).replace(/,/g, '');
  }

  formatValue(input) {
    const value = this.unformatNumber(input.value);
    if (value) {
      input.dataset.rawValue = value;
      input.value = this.formatNumber(value);
    }
  }

  onFocus(event) {
    const input = event.currentTarget;
    const rawValue = input.dataset.rawValue || this.unformatNumber(input.value);
    input.value = rawValue;
    input.type = 'number';
  }

  onBlur(event) {
    const input = event.currentTarget;
    input.type = 'text';
    input.dataset.rawValue = input.value;
    input.value = this.formatNumber(input.value);
  }

  formatNumberInput(event) {
    // Store raw value while typing
    event.currentTarget.dataset.rawValue = event.currentTarget.value;
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('.range-slider input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    // if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    // if (minInput.value) maxInput.setAttribute('min', minInput.value);
    // if (minInput.value === '') maxInput.setAttribute('min', 0);
    // if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    let minPrice = inputs[0].value;
    let maxPrice = inputs[1].value;
    if (minPrice > maxPrice) {
      let tempValue = maxPrice;
      maxPrice = minPrice;
      minPrice = tempValue;
      return true
    }
    return false
  }

  adjustToValidValues(input) {
    const isRange = (input.type == "range");
    const rawValue = isRange ? input.value : (input.dataset.rawValue || this.unformatNumber(input.value));
    const value = Number(rawValue);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    
    if (value < min) {
      input.value = min;
      if (!isRange) input.dataset.rawValue = min;
    }
    if (value > max) {
      input.value = max;
      if (!isRange) input.dataset.rawValue = max;
    }

    const rangeInputs = this.querySelectorAll('.range-slider input');
    const numberInputs = this.querySelectorAll('.number-slider input');
    if (rangeInputs.length < 2 || numberInputs.length < 2) {
      this.syncBottomSheetReadoutFromInputs();
      return;
    }
    const minRangeInput = rangeInputs[0];
    const maxRangeInput = rangeInputs[1];
    const minNumberInput = numberInputs[0];
    const maxNumberInput = numberInputs[1];

    let minPrice = isRange ? rangeInputs[0].value : (numberInputs[0].dataset.rawValue || this.unformatNumber(numberInputs[0].value));
    let maxPrice = isRange ? rangeInputs[1].value : (numberInputs[1].dataset.rawValue || this.unformatNumber(numberInputs[1].value));

    if (Number(minPrice) > Number(maxPrice)) {
      let tempValue = maxPrice;
      maxPrice = minPrice;
      minPrice = tempValue;
      minRangeInput.value = minPrice;
      minNumberInput.value = this.formatNumber(minPrice);
      minNumberInput.dataset.rawValue = minPrice;
      maxRangeInput.value = maxPrice;
      maxNumberInput.value = this.formatNumber(maxPrice);
      maxNumberInput.dataset.rawValue = maxPrice;
    } else {
      if (input.classList.contains("rulerinput-min")) {
        if (isRange) {
          minNumberInput.value = this.formatNumber(minPrice);
          minNumberInput.dataset.rawValue = minPrice;
        } else {
          minRangeInput.value = minPrice;
        }
      }
      if (input.classList.contains("rulerinput-max")) {
        if (isRange) {
          maxNumberInput.value = this.formatNumber(maxPrice);
          maxNumberInput.dataset.rawValue = maxPrice;
        } else {
          maxRangeInput.value = maxPrice;
        }
      }
    }
    
    if (!isRange && document.activeElement !== input) {
      input.value = this.formatNumber(input.dataset.rawValue || input.value);
    }
    this.syncBottomSheetReadoutFromInputs();
  }
}

customElements.get('price-range') || customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this._abort = new AbortController();
    const signal = this._abort.signal;
    const facetLink = this.querySelector('a');
    // Null-guard: a missing <a> child should not throw.
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this), { signal });
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    }, { signal });
  }

  disconnectedCallback() {
    if (this._abort) this._abort.abort();
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!form || typeof form.onActiveFilterClick !== 'function') return;
    form.onActiveFilterClick(event);
  }
}

customElements.get('facet-remove') || customElements.define('facet-remove', FacetRemove);

/**
 * Collection hero shortcuts use the same Section Rendering pipeline as the
 * regular facets. Keeping this delegated means the controls remain AJAX-first
 * even when Shopify replaces the grid/facet markup, while their hrefs still
 * provide a normal navigation fallback when JavaScript is unavailable.
 */
function syncCollectionQuickFilters(searchParams, pathname = window.location.pathname) {
  const params = new URLSearchParams(searchParams || '');
  const activeSize = params.get('filter.p.m.custom.screen_size') || '';
  const nav = document.querySelector('.macbook-pro-hero__choices[data-collection-base-path]');
  const collectionBasePath = nav?.dataset.collectionBasePath || '';
  const m5Constraint = nav?.dataset.m5Constraint || 'M5';
  const constraint = params.get('constraint') || '';
  let activeProcessor = '';
  if (collectionBasePath && pathname === collectionBasePath && constraint === m5Constraint) activeProcessor = 'm5';
  if (collectionBasePath && pathname === `${collectionBasePath}/m5-pro`) activeProcessor = 'm5-pro';
  if (collectionBasePath && pathname === `${collectionBasePath}/m5-max`) activeProcessor = 'm5-max';

  document.querySelectorAll('[data-collection-quick-filter]').forEach((link) => {
    const value = link.dataset.collectionQuickFilter || '';
    const kind = link.dataset.collectionFilterKind || '';
    const isActive =
      (kind === 'screen-size' && value === activeSize) ||
      (kind === 'processor-family' && value === activeProcessor);

    if (collectionBasePath) {
      const destination = new URL(link.href, window.location.origin);
      if (kind === 'screen-size') {
        destination.pathname = activeProcessor && activeProcessor !== 'm5' ? `${collectionBasePath}/${activeProcessor}` : collectionBasePath;
        destination.search = '';
        if (activeProcessor === 'm5') destination.searchParams.set('constraint', m5Constraint);
        if (!isActive) destination.searchParams.set('filter.p.m.custom.screen_size', value);
      } else if (kind === 'processor-family') {
        destination.pathname = !isActive && value && value !== 'm5' ? `${collectionBasePath}/${value}` : collectionBasePath;
        destination.search = '';
        if (!isActive && value === 'm5') destination.searchParams.set('constraint', m5Constraint);
        if (activeSize) destination.searchParams.set('filter.p.m.custom.screen_size', activeSize);
      } else if (kind === 'reset') {
        destination.pathname = collectionBasePath;
        destination.search = '';
      }
      destination.hash = 'ProductGridContainer';
      link.href = destination.toString();
    }

    link.classList.toggle('is-active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
      if (link.dataset.removeLabel) link.setAttribute('aria-label', link.dataset.removeLabel);
    } else {
      link.removeAttribute('aria-current');
      link.removeAttribute('aria-label');
    }
  });
}

/**
 * Preserve campaign/debug query parameters while quick filters replace Shopify
 * facet state. Filter, sort and pagination keys intentionally come from the
 * destination link so an old collection state cannot leak into the new view.
 */
function collectionQuickFilterSearchParams(link) {
  const destination = new URL(link.href, window.location.origin);
  const mergedParams = new URLSearchParams(destination.search);
  const currentParams = new URLSearchParams(window.location.search);

  currentParams.forEach((value, key) => {
    const isCollectionState =
      key === 'page' ||
      key === 'sort_by' ||
      key === 'section_id' ||
      key === 'constraint' ||
      key.startsWith('filter.');

    if (!isCollectionState && !mergedParams.has(key)) {
      mergedParams.append(key, value);
    }
  });

  return mergedParams;
}

/** Keep real link destinations attribution-safe for copy/open-in-new-tab and JS fallback. */
function syncCollectionQuickFilterHrefs() {
  document.querySelectorAll('[data-collection-quick-filter]').forEach((link) => {
    const url = new URL(link.href, window.location.origin);
    url.search = collectionQuickFilterSearchParams(link).toString();
    link.href = url.toString();
  });
}

function prefetchCollectionQuickFilter(link) {
  if (!link || !document.getElementById('product-grid')) return;
  const url = new URL(link.href, window.location.origin);
  const basePath = link.closest('[data-collection-base-path]')?.dataset.collectionBasePath || window.location.pathname;
  const isCollectionPath = url.pathname === basePath || url.pathname.startsWith(`${basePath}/`);
  if (url.origin !== window.location.origin || !isCollectionPath) return;

  const searchParams = collectionQuickFilterSearchParams(link);
  FacetFiltersForm.getSections().forEach((section) => {
    const sectionUrl = `${url.pathname}?section_id=${section.section}&${searchParams.toString()}`;
    FacetFiltersForm.prefetchSection(sectionUrl);
  });
}

function collectionQuickFilterFromEvent(event) {
  const target = event.target;
  return target && typeof target.closest === 'function'
    ? target.closest('[data-collection-quick-filter]')
    : null;
}

document.addEventListener('pointerenter', (event) => {
  prefetchCollectionQuickFilter(collectionQuickFilterFromEvent(event));
}, true);

document.addEventListener('focusin', (event) => {
  prefetchCollectionQuickFilter(collectionQuickFilterFromEvent(event));
});

document.addEventListener('touchstart', (event) => {
  prefetchCollectionQuickFilter(collectionQuickFilterFromEvent(event));
}, { passive: true, capture: true });

function warmCollectionQuickFilters() {
  syncCollectionQuickFilterHrefs();
  document.querySelectorAll('.macbook-pro-hero__choice:not([aria-current="page"])').forEach((link) => {
    prefetchCollectionQuickFilter(link);
  });
}

let collectionQuickFilterSequence = 0;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', warmCollectionQuickFilters, { once: true });
} else {
  warmCollectionQuickFilters();
}

document.addEventListener('click', (event) => {
  const link = collectionQuickFilterFromEvent(event);
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const productGrid = document.getElementById('ProductGridContainer');
  if (!productGrid || !document.getElementById('product-grid')) return;

  const url = new URL(link.href, window.location.origin);
  const collectionBasePath = link.closest('[data-collection-base-path]')?.dataset.collectionBasePath || window.location.pathname;
  const isCollectionPath = url.pathname === collectionBasePath || url.pathname.startsWith(`${collectionBasePath}/`);
  if (url.origin !== window.location.origin || !isCollectionPath) return;

  event.preventDefault();
  FacetFiltersForm.cancelPendingSubmitsEverywhere();
  const requestSequence = ++collectionQuickFilterSequence;

  const searchParams = collectionQuickFilterSearchParams(link).toString();
  syncCollectionQuickFilters(searchParams, url.pathname);
  const selector = link.closest('.macbook-pro-hero__choices');
  if (selector) selector.setAttribute('aria-busy', 'true');
  const gridRect = productGrid.getBoundingClientRect();
  const gridWasVisible = gridRect.top < window.innerHeight * 0.9 && gridRect.bottom > 0;
  productGrid.setAttribute('aria-busy', 'true');
  productGrid.classList.remove('collection-quick-filter--revealing');
  productGrid.classList.add('collection-quick-filter--pending');

  document.addEventListener('facets:grid-updated', () => {
    if (requestSequence !== collectionQuickFilterSequence) return;
    syncCollectionQuickFilters(window.location.search.slice(1));
    if (selector) selector.removeAttribute('aria-busy');
    const nextGrid = document.getElementById('ProductGridContainer');
    if (nextGrid) {
      nextGrid.removeAttribute('aria-busy');
      nextGrid.classList.remove('collection-quick-filter--pending');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Keep the viewport stable when products are already visible. On shorter
      // screens, move to the refreshed results only when the grid was offscreen.
      if (!gridWasVisible) {
        window.requestAnimationFrame(() => {
          nextGrid.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      }
    }
  }, { once: true });

  FacetFiltersForm.renderPage(searchParams, { quickFilter: true, requestSequence, basePath: url.pathname }, true);
});

document.addEventListener('facets:grid-updated', () => {
  syncCollectionQuickFilters(window.location.search.slice(1));
  warmCollectionQuickFilters();
});

// Sort Pills Handler - Using Event Delegation for dynamic content
document.addEventListener('click', function(e) {
  const pill = e.target.closest('.sort-pill');
  if (!pill) return;
  
  e.preventDefault();
  
  const container = pill.closest('.sort-pills');
  const form = pill.closest('form');
  const hiddenInput = form ? form.querySelector('input[name="sort_by"]') : null;
  const sortValue = pill.dataset.sortValue;
  
  // Update active state
  if (container) {
    container.querySelectorAll('.sort-pill').forEach(p => {
      p.classList.remove('sort-pill--active');
      p.setAttribute('aria-checked', 'false');
    });
  }
  pill.classList.add('sort-pill--active');
  pill.setAttribute('aria-checked', 'true');
  
  // Update hidden input value and trigger form update
  if (hiddenInput) {
    hiddenInput.value = sortValue;
    // Trigger input event for facet form
    hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
});

// ============================================
// BOTTOM SHEET - Mobile Filter & Sort Handler
// ============================================
class BottomSheetManager {
  constructor() {
    this.overlay = document.querySelector('[data-sheet-overlay]');
    this.sheets = document.querySelectorAll('[data-sheet]');
    this.activeSheet = null;
    this.triggerElement = null;
    this.inertedElements = [];

    this.init();
  }
  
  init() {
    // Open sheet buttons
    document.querySelectorAll('[data-open-sheet]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const sheetId = e.currentTarget.dataset.openSheet;
        this.openSheet(sheetId);
      });
    });
    
    // Close sheet buttons
    document.querySelectorAll('[data-close-sheet]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        // If this is the Apply button in the filter sheet, submit the form first
        if (btn.classList.contains('bottom-sheet__apply-btn')) {
          const filterSheet = document.getElementById('filter-sheet');
          if (filterSheet) {
            const sheetForm = filterSheet.querySelector('form');
            if (sheetForm) {
              FacetFiltersForm.cancelPendingSubmitsEverywhere();
              const mergeIds =
                sheetForm.id === 'FacetFiltersFormMobileSheet'
                  ? ['FacetSortForm', 'FacetFiltersFormMobileSheet']
                  : [sheetForm.id];
              const mergedParams = mergeFacetFormParams(mergeIds);
              stripDefaultPriceFiltersFromParams(mergedParams, sheetForm.id);
              const qs = mergedParams.toString();
              this.closeSheet();
              FacetFiltersForm.renderPage(qs, null, true);
              return;
            }
          }
        }
        this.closeSheet();
      });
    });
    
    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeSheet());
    }
    
    // Handle "Remove all" button in bottom sheet (use event delegation for dynamic content)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bottom-sheet__clear-btn')) {
        e.preventDefault();
        const clearLink = e.target.closest('.bottom-sheet__clear-btn');
        const clearUrl = new URL(clearLink.href, window.location.origin);
        this.closeSheet();
        // Preserve the search term/type on search pages while removing facets.
        // Collection clear links have no query string, so their behavior stays
        // unchanged.
        FacetFiltersForm.renderPage(clearUrl.searchParams.toString(), null, true);
      }
    });
    
    // Shared swipe/drag - assets/bottom-sheet-shared.js (same feel as PDP accordion sheet)
    this.sheets.forEach(sheet => {
      if (typeof window.themeBottomSheet !== 'undefined' && window.themeBottomSheet.attachSwipeToClose) {
        window.themeBottomSheet.attachSwipeToClose({
          sheet,
          overlay: this.overlay,
          onClose: () => this.closeSheet(),
          enableExpand: false,
          attachContentSwipe: sheet.id !== 'filter-sheet',
        });
      }
    });
    
    // Sort option change handler
    document.querySelectorAll('[data-sort-option]').forEach(input => {
      input.addEventListener('change', (e) => this.handleSortChange(e));
    });

    /* Phase 7.3 - Document-level Escape handler. Each sheet has its
       own close pill / overlay-click already, but keyboard users
       expect Escape to close the active sheet (matches the pattern
       in search-bottom-sheet.liquid:269 and the native <dialog>
       behaviour). Bound to document so focus position inside any
       child input (e.g. price range) still routes to closeSheet(). */
    document.addEventListener('keydown', (e) => {
      if (!this.activeSheet) return;
      if (e.key === 'Tab') {
        this.trapFocus(e);
        return;
      }
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      this.closeSheet();
    });

    // Format price inputs with thousand separators
    this.setupPriceFormatting();
    this.setupFilterAccordionAnimation();
  }

  /* Native <details> toggles its hidden content before CSS can interpolate
     the 0fr → 1fr track, so mobile facet rows snap open. Animate the actual
     measured block height while keeping the bottom-sheet frame fixed. */
  setupFilterAccordionAnimation() {
    const sheet = document.getElementById('filter-sheet');
    if (!sheet || sheet.dataset.accordionAnimationBound === '1') return;
    sheet.dataset.accordionAnimationBound = '1';

    sheet.addEventListener('click', (event) => {
      const summary = event.target.closest('.bottom-sheet__filter-header');
      if (!summary || !sheet.contains(summary)) return;

      const details = summary.closest('details.bottom-sheet__filter-group');
      if (!details) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || typeof details.animate !== 'function') return;

      event.preventDefault();
      if (details.dataset.accordionAnimating === '1') return;
      details.dataset.accordionAnimating = '1';

      const content = details.querySelector('.bottom-sheet__filter-content');
      const opening = !details.open;
      const startHeight = details.getBoundingClientRect().height;

      /* Toggle only for measurement, within the same frame, so there is no
         visible flash. Closing stays open until its collapse animation ends. */
      details.open = opening;
      const endHeight = details.getBoundingClientRect().height;
      if (!opening) details.open = true;

      details.style.height = `${startHeight}px`;
      details.style.overflow = 'hidden';
      details.style.willChange = 'height';

      const panelAnimation = details.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: 320, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
      );

      if (content) {
        content.animate(
          opening
            ? [
                { opacity: 0, transform: 'translateY(-4px)' },
                { opacity: 1, transform: 'translateY(0)' },
              ]
            : [
                { opacity: 1, transform: 'translateY(0)' },
                { opacity: 0, transform: 'translateY(-4px)' },
              ],
          { duration: opening ? 240 : 180, easing: 'ease-out' }
        );
      }

      panelAnimation.finished
        .then(() => {
          if (!opening) details.open = false;
        })
        .catch(() => {})
        .finally(() => {
          details.style.height = '';
          details.style.overflow = '';
          details.style.willChange = '';
          delete details.dataset.accordionAnimating;
        });
    });
  }
  
  setupPriceFormatting() {
    document.querySelectorAll('.bottom-sheet__price-field input').forEach(input => {
      if (input.dataset.priceFormatBound === '1') return;
      input.dataset.priceFormatBound = '1';
      this.formatPriceInput(input);
      input.addEventListener('input', (e) => {
        this.formatPriceInput(e.target);
      });
      input.addEventListener('blur', (e) => {
        const rawValue = e.target.value.replace(/,/g, '');
        e.target.dataset.rawValue = rawValue;
      });
    });
  }
  
  formatPriceInput(input) {
    // Get raw number value
    let value = input.value.replace(/,/g, '');
    if (!value || isNaN(value)) return;
    
    // Format with thousand separators
    const formatted = Number(value).toLocaleString('en-US');
    input.value = formatted;
  }

  getFocusableElements(sheet = this.activeSheet) {
    if (!sheet) return [];
    return Array.from(sheet.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.activeSheet?.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  isolateSheet(sheet) {
    this.restoreBackground();
    let branch = sheet;

    while (branch?.parentElement) {
      const parent = branch.parentElement;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === branch || sibling === this.overlay || sibling.inert) return;
        sibling.inert = true;
        this.inertedElements.push(sibling);
      });
      if (parent === document.body) break;
      branch = parent;
    }
  }

  restoreBackground() {
    this.inertedElements.forEach((element) => {
      if (element?.isConnected) element.inert = false;
    });
    this.inertedElements = [];
  }

  openSheet(sheetId) {
    const sheet = document.getElementById(sheetId);
    if (!sheet) return;
    
    this.triggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.activeSheet = sheet;
    this.isolateSheet(sheet);
    sheet.classList.add('active');
    sheet.setAttribute('aria-hidden', 'false');
    if (this.overlay) this.overlay.classList.add('active');
    if (window.themeSheetScrollLock) {
      var scrollContainer = sheet.querySelector('.bottom-sheet__body') || sheet.querySelector('.bottom-sheet__content') || sheet;
      this._sheetLockId = window.themeSheetScrollLock.lock(scrollContainer);
    }
    window.requestAnimationFrame(() => {
      const closeButton = sheet.querySelector('[data-close-sheet]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    });
  }

  closeSheet() {
    if (this.activeSheet) {
      if (this.activeSheet.id === 'filter-sheet') {
        this.activeSheet.querySelectorAll('details.bottom-sheet__filter-group[open]').forEach((d) => {
          d.open = false;
        });
      }
      this.activeSheet.classList.remove('active');
      this.activeSheet.setAttribute('aria-hidden', 'true');
      this.activeSheet.style.transform = '';
    }
    if (this.overlay) this.overlay.classList.remove('active');
    if (this._sheetLockId != null && window.themeSheetScrollLock) {
      window.themeSheetScrollLock.unlock(this._sheetLockId);
      this._sheetLockId = null;
    }
    this.restoreBackground();
    this.activeSheet = null;
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus({ preventScroll: true });
    }
    this.triggerElement = null;
  }
  
  handleSortChange(e) {
    const sortValue = e.target.value;
    
    // Update all sort option labels
    document.querySelectorAll('.bottom-sheet__sort-option').forEach(label => {
      const input = label.querySelector('input');
      if (input.value === sortValue) {
        label.classList.add('active');
        // Add checkmark if not present
        if (!label.querySelector('.bottom-sheet__sort-check')) {
          const check = document.createElement('svg');
          check.className = 'bottom-sheet__sort-check';
          check.setAttribute('width', '20');
          check.setAttribute('height', '20');
          check.setAttribute('viewBox', '0 0 24 24');
          check.setAttribute('fill', 'none');
          check.setAttribute('stroke', 'currentColor');
          check.setAttribute('stroke-width', '2');
          check.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
          label.appendChild(check);
        }
      } else {
        label.classList.remove('active');
        const check = label.querySelector('.bottom-sheet__sort-check');
        if (check) check.remove();
      }
    });
    
    // Update URL with new sort
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('sort_by', sortValue);
    
    // Trigger the page render
    const searchParams = currentUrl.searchParams.toString();
    FacetFiltersForm.renderPage(searchParams, null, true);
    this.closeSheet();
  }
}

// Initialize bottom sheet manager when DOM is ready
let bottomSheetManager;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bottomSheetManager = new BottomSheetManager();
  });
} else {
  bottomSheetManager = new BottomSheetManager();
}

// Re-initialize after dynamic content updates.
// Guard against TDZ: SoldOutFilterHandler is declared with `const` BELOW
// this listener, so if the event fired during initial parse it would
// throw ReferenceError. typeof check is safe even in TDZ in modern engines
// only via the global object - use a runtime check on declared-after refs.
document.addEventListener('loadmore:complete', () => {
  if (typeof SoldOutFilterHandler !== 'undefined' && SoldOutFilterHandler.apply) {
    SoldOutFilterHandler.apply();
  }
});

// ============================================
// SOLD OUT FILTER HANDLER
// Hides products that have available variants when "sold out" filter is active
// Shopify's availability filter works at variant level, not product level
// This fixes the issue where products with mixed availability show up
// ============================================
const SoldOutFilterHandler = {
  // Check if the sold out filter is active
  // Shopify uses: filter.v.availability=0 or filter.p.v.availability=0
  isSoldOutFilterActive() {
    const urlParams = new URLSearchParams(window.location.search);
    
    for (const [key, value] of urlParams.entries()) {
      // Check for Shopify's availability filter format
      // Common patterns: filter.v.availability, filter.p.v.availability
      if (key.toLowerCase().includes('availability')) {
        // Value "0" or "false" means out of stock / sold out
        if (value === '0' || value === 'false') {
          return true;
        }
      }
    }
    return false;
  },
  
  // Check if "in stock" filter is active
  isInStockFilterActive() {
    const urlParams = new URLSearchParams(window.location.search);
    
    for (const [key, value] of urlParams.entries()) {
      if (key.toLowerCase().includes('availability')) {
        // Value "1" or "true" means in stock / available
        if (value === '1' || value === 'true') {
          return true;
        }
      }
    }
    return false;
  },
  
  // Apply the filter logic
  apply() {
    const productCards = document.querySelectorAll('.pcard[data-has-available-variant]');
    const soldOutActive = this.isSoldOutFilterActive();
    const inStockActive = this.isInStockFilterActive();
    
    
    // If BOTH filters are active, don't apply any custom hiding
    // because user wants to see all products (both in stock and out of stock)
    const bothFiltersActive = soldOutActive && inStockActive;
    
    productCards.forEach(card => {
      const hasAvailableVariant = card.dataset.hasAvailableVariant === 'true';
      const allSoldOut = card.dataset.allSoldOut === 'true';
      const gridItem = card.closest('.grid__item');
      
      if (!gridItem) return;
      
      // Reset visibility first
      gridItem.style.display = '';
      gridItem.classList.remove('hidden-by-filter');
      
      // If both filters are active, skip custom hiding (show all)
      if (bothFiltersActive) {
        return;
      }
      
      // If sold out filter is active, hide products that have ANY available variant
      // Because we only want to show products where ALL variants are sold out
      if (soldOutActive && hasAvailableVariant) {
        gridItem.style.display = 'none';
        gridItem.classList.add('hidden-by-filter');
      }
      
      // If in stock filter is active, hide products that are completely sold out
      if (inStockActive && allSoldOut) {
        gridItem.style.display = 'none';
        gridItem.classList.add('hidden-by-filter');
      }
    });
  }
};

// Apply on initial page load
document.addEventListener('DOMContentLoaded', () => {
  SoldOutFilterHandler.apply();
});

// Apply after facets are updated (AJAX navigation)
document.addEventListener('facets:updated', () => {
  // Small delay to ensure DOM is updated
  setTimeout(() => {
    SoldOutFilterHandler.apply();
  }, 100);
});

// Re-apply when ProductGridContainer content changes (throttled via rAF)
let productGridRafId = 0;
const productGridObserver = new MutationObserver((mutations) => {
  const hasAdditions = mutations.some(m => m.type === 'childList' && m.addedNodes.length > 0);
  if (!hasAdditions) return;
  if (productGridRafId) cancelAnimationFrame(productGridRafId);
  productGridRafId = requestAnimationFrame(() => {
    productGridRafId = 0;
    SoldOutFilterHandler.apply();
  });
});

// Start observing after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) {
      productGridObserver.observe(productGrid, { childList: true, subtree: true });
    }
  });
} else {
  const productGrid = document.getElementById('ProductGridContainer');
  if (productGrid) {
    productGridObserver.observe(productGrid, { childList: true, subtree: true });
  }
}
